import { CartAddEvent } from '@theme/events';
import { readEaLinePropertiesFromEmbeddedScript } from '@theme/ea-cart';

function qs(root, sel) {
  return /** @type {HTMLElement | null} */ (root.querySelector(sel));
}

function qsa(root, sel) {
  return /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll(sel));
}

function parseProductJson(sectionId) {
  const el = document.getElementById(`ProductJson-${sectionId}`);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || el.innerHTML || '{}');
  } catch {
    return null;
  }
}

function initDefaults(form, product) {
  try {
    const fieldsets = qsa(form, '.variant-option');
    fieldsets.forEach((fs) => {
      const has = qs(fs, 'input[type="radio"]:checked');
      if (has) return;
      const inputs = fs.querySelectorAll('input[type="radio"]:not(.disabled):not([disabled])');
      if (!inputs.length) return;
      for (const input of inputs) {
        const radio = /** @type {HTMLInputElement} */ (input);
        if (!radio.disabled) {
          radio.checked = true;
          try {
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          } catch {}
          break;
        }
      }
    });
  } catch {}
}

function resolveVariant(form) {
  const sid = form.getAttribute('data-section-id') || '';
  const product = parseProductJson(sid);
  if (!product) return null;

  const optionCount = Array.isArray(product.options) ? product.options.length : 0;
  if (optionCount > 0) initDefaults(form, product);

  const selected = [];
  qsa(form, '.variant-option').forEach((fs, idx) => {
    const sel = qs(fs, 'input[type="radio"]:checked');
    if (sel) selected[idx] = /** @type {HTMLInputElement} */ (sel).value;
  });

  if (product.variants && product.variants.length) {
    if (optionCount === 0) {
      for (const v of product.variants) if (v.available) return v;
      return product.variants[0];
    }
    let matched = null;
    for (const vr of product.variants) {
      let ok = true;
      if (optionCount >= 1 && (selected[0] === undefined || vr.option1 !== selected[0])) ok = false;
      if (ok && optionCount >= 2 && (selected[1] === undefined || vr.option2 !== selected[1])) ok = false;
      if (ok && optionCount >= 3 && (selected[2] === undefined || vr.option3 !== selected[2])) ok = false;
      if (ok) {
        matched = vr;
        break;
      }
    }
    if (!matched || matched.available === false) {
      for (const v of product.variants) if (v.available) return v;
      return product.variants[0];
    }
    return matched;
  }
  return null;
}

function onClick(e) {
  const target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
  if (!target) return;
  const btn = target.closest?.('button.shop-the-look-add');
  if (!(btn instanceof HTMLButtonElement)) return;
  const formId = btn.getAttribute('form');
  const form = formId ? document.getElementById(formId) : btn.closest('form.shop-the-look-form');
  if (!(form instanceof HTMLFormElement)) return;
  e.preventDefault();
  btn.classList.add('btn--loading');
  btn.setAttribute('aria-busy', 'true');

  const matched = resolveVariant(form);
  if (!matched || matched.available === false) {
    btn.classList.remove('btn--loading');
    btn.removeAttribute('aria-busy');
    return;
  }

  const run = async () => {
    try {
      const eaProps = readEaLinePropertiesFromEmbeddedScript(form);
      const item = {
        id: Number(matched.id),
        quantity: 1,
        ...(Object.keys(eaProps).length > 0 ? { properties: eaProps } : {}),
      };
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      const data = await res.json();

      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const animationMs = 600;
      const dispatchCartAndOpen = () => {
        try {
          document.dispatchEvent(
            new CartAddEvent({}, 'shop-the-look', {
              source: 'shop-the-look',
              itemCount: 1,
              sections: data?.sections,
            }),
          );
        } catch {}
        const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        }
      };
      if (reduced) {
        dispatchCartAndOpen();
      } else {
        setTimeout(dispatchCartAndOpen, animationMs + 100);
      }

      // Micro interaction
      const labelEl = btn.querySelector('.shop-the-look-label');
      const previousText = labelEl ? labelEl.textContent : btn.textContent;
      btn.classList.add('atc-added');
      if (labelEl) labelEl.textContent = (window.Theme && Theme.translations && Theme.translations.added) || 'Added';
      else btn.textContent = 'Added';
      setTimeout(() => {
        btn.classList.remove('atc-added');
        if (labelEl) labelEl.textContent = previousText || '';
        else btn.textContent = previousText || '';
      }, reduced ? 300 : 1600);
    } catch {
      // noop
    } finally {
      btn.classList.remove('btn--loading');
      btn.removeAttribute('aria-busy');
    }
  };
  run();
}

function onChange(e) {
  const target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
  if (!target) return;
  const form = target.closest?.('form.shop-the-look-form');
  if (!(form instanceof HTMLFormElement)) return;
  // Live update low-stock badge on variant change
  try {
    const variant = resolveVariant(form);
    updateLowStockForForm(form, variant);
  } catch {
    // noop
  }
}

document.addEventListener('click', onClick, true);
document.addEventListener('change', onChange, true);

/**
 * Updates the low stock badge for a given form/row based on the selected variant.
 * @param {HTMLFormElement} form
 * @param {any} variant
 */
function updateLowStockForForm(form, variant) {
  try {
    const row = form.closest('.stl-row');
    if (!row) return;
    const badgeWrap = /** @type {HTMLElement | null} */ (row.querySelector('.stl-low-stock'));
    if (!badgeWrap) return;
    const wrapper = /** @type {HTMLElement | null} */ (row.closest('[data-low-threshold]'));
    if (!wrapper) return;
    const showSetting = (wrapper.getAttribute('data-show-low') || '').toString() === 'true';
    if (!showSetting) {
      badgeWrap.hidden = true;
      return;
    }
    const th = parseInt(wrapper.getAttribute('data-low-threshold') || '3', 10);
    const managed = variant && variant.inventory_management === 'shopify';
    const qty = variant && typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null;
    const show = !!(managed && qty !== null && qty > 0 && qty <= th);
    badgeWrap.hidden = !show;
    if (show) {
      const countEl = /** @type {HTMLElement | null} */ (badgeWrap.querySelector('.lsi-count'));
      if (countEl) countEl.textContent = String(qty);
    }
  } catch {
    // noop
  }
}


